import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  XCircle,
  Database,
  Cloud,
  GitCompare,
  SkipForward,
  RotateCcw,
  MapPin,
  ChevronDown,
  ChevronRight,
  FolderPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AddToCollectionDialog } from "@/components/collections/AddToCollectionDialog";
import type { WizardState, WizardAction } from "../ImportPage";

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}

export function ResultsStep({ state, dispatch }: Props) {
  const { importResult } = state;
  const [showGeoFailures, setShowGeoFailures] = useState(false);
  const [showCollectionDialog, setShowCollectionDialog] = useState(false);

  if (!importResult) return null;

  const hasErrors = importResult.errors.length > 0;
  const success = importResult.localUpserted > 0;
  const geocodedCount = importResult.geocodedCount ?? 0;
  const geocodeFailures = importResult.geocodeFailures ?? [];

  return (
    <div className="space-y-6">
      {/* Status banner */}
      <div
        className={`rounded-lg border p-4 flex items-center gap-3 ${
          success
            ? "border-green-500/50 bg-green-500/10"
            : "border-destructive/50 bg-destructive/10"
        }`}
      >
        {success ? (
          <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 shrink-0" />
        ) : (
          <XCircle className="w-6 h-6 text-destructive shrink-0" />
        )}
        <div>
          <p
            className={`font-medium ${success ? "text-green-700 dark:text-green-300" : "text-destructive"}`}
          >
            {success ? "Import completed successfully" : "Import failed"}
          </p>
          <p className="text-sm text-muted-foreground">
            {importResult.totalRows} total rows processed
          </p>
        </div>
      </div>

      {/* Result cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Database className="w-5 h-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">
                {importResult.localUpserted}
              </p>
              <p className="text-xs text-muted-foreground">
                Local Upserted
              </p>
            </div>
          </CardContent>
        </Card>

        {importResult.prodUpserted !== null && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Cloud className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-2xl font-semibold">
                  {importResult.prodUpserted}
                </p>
                <p className="text-xs text-muted-foreground">
                  Production Upserted
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {importResult.matchesFound !== null && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <GitCompare className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-2xl font-semibold">
                  {importResult.matchesFound}
                </p>
                <p className="text-xs text-muted-foreground">
                  Matches Found
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {geocodedCount > 0 && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <MapPin className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-2xl font-semibold">{geocodedCount}</p>
                <p className="text-xs text-muted-foreground">
                  Addresses Geocoded
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {importResult.skippedRows > 0 && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <SkipForward className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-semibold">
                  {importResult.skippedRows}
                </p>
                <p className="text-xs text-muted-foreground">
                  Rows Skipped
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Errors */}
      {hasErrors && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-destructive flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Errors ({importResult.errors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {importResult.errors.map((err, i) => (
                <p key={i} className="text-sm text-destructive font-mono">
                  {err}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Geocoding failures */}
      {geocodeFailures.length > 0 && (
        <Card>
          <CardHeader>
            <button
              className="flex items-center gap-2 w-full text-left"
              onClick={() => setShowGeoFailures(!showGeoFailures)}
            >
              {showGeoFailures ? (
                <ChevronDown className="w-4 h-4 text-yellow-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-yellow-600" />
              )}
              <CardTitle className="text-base text-yellow-700 dark:text-yellow-400">
                Geocoding Failures ({geocodeFailures.length})
              </CardTitle>
            </button>
          </CardHeader>
          {showGeoFailures && (
            <CardContent>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {geocodeFailures.map((f, i) => (
                  <p key={i} className="text-sm font-mono text-muted-foreground">
                    <span className="text-yellow-600 dark:text-yellow-400">
                      Row {f.row}:
                    </span>{" "}
                    "{f.address}" — {f.reason}
                  </p>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-center pt-4">
        <Button
          variant="outline"
          onClick={() => dispatch({ type: "RESET" })}
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Import Another
        </Button>
        {importResult.locationIds && importResult.locationIds.length > 0 && (
          <Button
            variant="outline"
            onClick={() => setShowCollectionDialog(true)}
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            Add to Collection
          </Button>
        )}
        <Button asChild>
          <Link to="/dashboard">
            <MapPin className="w-4 h-4 mr-2" />
            View Map
          </Link>
        </Button>
      </div>

      {importResult.locationIds && importResult.locationIds.length > 0 && (
        <AddToCollectionDialog
          open={showCollectionDialog}
          onOpenChange={setShowCollectionDialog}
          locationIds={importResult.locationIds}
        />
      )}
    </div>
  );
}
